import React, {
    useState,
    useEffect,
    useContext,
    useMemo,
    useCallback,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { IconButton, Divider, Box } from '@mui/material'
import {
    formatDate,
    getOffset,
    usePagination,
    useSearchParams,
    Pagination,
    SearchFilter,
    TenantsIcon,
    MoreVertIcon,
    AddIcon,
} from '../components'
import { getTenants } from '../api'
import { Tenant, Tenants } from '../model'
import { AppContext } from '../context'
import { translate } from '../i18n'
import TenantMenu from './TenantMenu'
import { Griddable } from '../grid'

export default function TenantsList() {
    const history = useHistory()
    const context = useContext(AppContext)
    const query = new URLSearchParams(useLocation().search)
    const params = useSearchParams(query)

    const [status, setStatus] = useState<'loading' | 'loaded' | Error>(
        'loading'
    )
    const [data, setData] = useState<Tenants>()
    const clearCallback = useCallback(() => setData(undefined), [])
    const { page, pageSize, onChangedPage, onChangedPageSize } =
        usePagination(clearCallback)

    const allowedCreate = useMemo(
        () => context.isGranted('TenantsCreate'),
        [context]
    )

    const retrievePromise = useCallback((): Promise<Tenants> => {
        const offset = getOffset(page, pageSize)
        return getTenants({ ...params, pageSize, offset })
    }, [page, pageSize, params])

    const reload = useCallback(() => {
        setData(undefined)
        setStatus('loading')
        retrievePromise()
            .then((response) => {
                setData(response)
                setStatus('loaded')
            })
            .catch((error) => {
                setStatus(error)
            })
    }, [retrievePromise])

    useEffect(reload, [reload])

    const onClicked = useCallback(
        (tenant: Tenant) => {
            history.push(`/tenants/${tenant.id}/edit`)
        },
        [history]
    )

    return (
        <Pagination
            title={translate('tenants')}
            icon={<TenantsIcon />}
            page={page}
            pageSize={pageSize}
            count={data ? data.items.length : 0}
            total={data ? data.total : 0}
            onChangedPage={onChangedPage}
            onChangedPageSize={onChangedPageSize}
            buttons={[
                {
                    text: translate('buttons.add'),
                    icon: <AddIcon />,
                    path: '/tenants/new',
                    available: allowedCreate,
                    variant: 'primary',
                },
            ]}
        >
            <SearchFilter
                label={translate('tenants.filter') as string}
                query={query}
            />
            <Box mx={-2} mt={2}>
                <Divider />
                <Griddable
                    items={data ? data.items : []}
                    loading={status === 'loading'}
                    error={status instanceof Error ? status.message : undefined}
                    empty={translate('tenants.empty') as string}
                    getId={(tenant: Tenant) => tenant.id}
                    clickable={{
                        onClick: onClicked,
                    }}
                    refreshable={{
                        columnId: 'actions',
                        onClick: reload,
                    }}
                    columns={[
                        {
                            title: translate('tenants.name') as string,
                            converter: (tenant: Tenant) => tenant.name,
                            id: 'name',
                        },
                        {
                            title: translate('tenants.active.title') as string,
                            converter: (tenant: Tenant) =>
                                translate(`tenants.active.${tenant.active}`),
                            id: 'status',
                        },
                        {
                            title: translate('tenants.created_at') as string,
                            converter: (tenant: Tenant) =>
                                formatDate({ date: tenant.created_at }),
                            id: 'created_at',
                        },
                        {
                            title: (
                                <IconButton
                                    size="small"
                                    style={{ visibility: 'hidden' }}
                                    disabled
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            ),
                            converter: (tenant: Tenant) => (
                                <TenantMenu key={tenant.id} tenant={tenant} />
                            ),
                            id: 'actions',
                            fixed: 'right',
                            width: 60,
                            textAlign: 'center',
                        },
                    ]}
                />
            </Box>
        </Pagination>
    )
}
